/**
 * Iter-233 — Client Team Members page
 *
 * Lets a client owner invite additional Company Users (Marketing Manager,
 * Event Coordinator, Operations, Regional, Showroom, etc).
 *
 * Seat policy (grandfathered for all existing clients):
 *   1 Primary + 1 Additional = 2 included seats.
 *   Inviting the 3rd seat surfaces an upgrade modal driven by the
 *   backend's 402 Payment Required response.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Users, Plus, Loader2, ChevronLeft, X, Crown, CheckCircle2,
  Mail, UserCheck, Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: 'editor', label: 'Editor — full workspace access' },
  { value: 'viewer', label: 'Viewer — read-only access' },
];

const TITLE_SUGGESTIONS = [
  'Marketing Manager',
  'Event Coordinator',
  'Operations Manager',
  'Regional Manager',
  'Showroom Manager',
];

const SeatBar = ({ used, total }) => {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const near = used >= total;
  return (
    <div data-testid="seat-bar">
      <div className="flex items-baseline justify-between text-xs mb-1">
        <span className="font-bold uppercase tracking-wider text-slate-500">Seats</span>
        <span className={`font-bold ${near ? 'text-rose-600' : 'text-slate-900'}`} data-testid="seats-count">
          {used} / {total}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${near ? 'bg-rose-500' : 'bg-cyan-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const UpgradeModal = ({ open, onClose, info }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4" data-testid="upgrade-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200">
        <div className="flex items-center gap-2 text-amber-700 mb-3">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-bold uppercase tracking-wider text-xs">Upgrade Required</span>
        </div>
        <h2 className="text-xl font-extrabold text-slate-900 mb-2">You've used all your included seats</h2>
        <p className="text-sm text-slate-600 mb-4">
          {info?.message || `You're using all ${info?.seats_total || 2} included company-user seats. Upgrade to add more team members.`}
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4 space-y-1 text-sm text-slate-700">
          <div>Primary user — included</div>
          <div>+ 1 Additional Company User — included</div>
        Need a 3rd seat? Contact your iEvntür account manager to upgrade.
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} data-testid="upgrade-modal-close">Close</Button>
          <Button
            onClick={() => { window.location.href = 'mailto:accounts@ievntur.com?subject=Add%20Team%20Member%20Seat'; }}
            data-testid="upgrade-modal-contact"
          >
            <Mail className="w-4 h-4 mr-1.5" /> Contact Account Manager
          </Button>
        </div>
      </div>
    </div>
  );
};

const InviteForm = ({ disabled, onInvited, onSeatLimit }) => {
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', title: '', org_role: 'editor' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setBusy(true);
    try {
      const r = await api.post('/client/company/members', form);
      toast.success(`Invited ${form.email}`);
      setForm({ email: '', first_name: '', last_name: '', title: '', org_role: 'editor' });
      onInvited?.(r.data);
    } catch (err) {
      if (err?.response?.status === 402) {
        onSeatLimit?.(err.response.data?.detail || err.response.data);
      } else {
        toast.error(err?.response?.data?.detail || 'Invite failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3"
      data-testid="invite-team-form"
    >
      <h3 className="text-base font-extrabold text-slate-900">Invite a Company User</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-xs">Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="teammate@yourcompany.com"
            required
            data-testid="input-invite-email"
          />
        </div>
        <div>
          <Label className="text-xs">First Name</Label>
          <Input
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            data-testid="input-invite-first"
          />
        </div>
        <div>
          <Label className="text-xs">Last Name</Label>
          <Input
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            data-testid="input-invite-last"
          />
        </div>
        <div>
          <Label className="text-xs">Title / Role</Label>
          <Input
            value={form.title}
            list="title-suggestions"
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Marketing Manager"
            data-testid="input-invite-title"
          />
          <datalist id="title-suggestions">
            {TITLE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div>
          <Label className="text-xs">Workspace Permission</Label>
          <select
            value={form.org_role}
            onChange={(e) => setForm({ ...form, org_role: e.target.value })}
            className="block w-full mt-1 border border-slate-200 rounded-md h-10 px-2 text-sm"
            data-testid="select-invite-role"
          >
            {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <Button type="submit" disabled={disabled || busy} data-testid="submit-invite-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        <span className="ml-1.5">Send Invite</span>
      </Button>
      {disabled && (
        <p className="text-xs text-rose-600 font-semibold">Seat limit reached. Remove a member or upgrade to invite more.</p>
      )}
    </form>
  );
};

const ClientTeamMembers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ members: [], seats_used: 0, seats_total: 2 });
  const [upgradeInfo, setUpgradeInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/client/company/members');
      setData(r.data || { members: [], seats_used: 0, seats_total: 2 });
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRemove = async (m) => {
    if (!window.confirm(`Remove ${m.email}?`)) return;
    try {
      await api.delete(`/client/company/members/${m.user_id}`);
      toast.success(`${m.email} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  const seatsRemaining = Math.max(0, (data.seats_total || 0) - (data.seats_used || 0));
  const atLimit = seatsRemaining <= 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16" data-testid="client-team-members">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <button
          onClick={() => navigate('/client/home')}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="back-to-home"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Home
        </button>

        <header className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-cyan-700 text-xs font-bold uppercase tracking-wider">
                <Users className="w-4 h-4" /> Team Members
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1">Your Company Workspace</h1>
              <p className="text-sm text-slate-500 mt-1">
                Invite teammates such as Marketing Managers, Event Coordinators, Operations Managers, Regional Managers,
                or Showroom Managers. Your account includes <strong>{data.seats_total}</strong> seats.
              </p>
            </div>
            <div className="min-w-[180px]">
              <SeatBar used={data.seats_used} total={data.seats_total} />
            </div>
          </div>
        </header>

        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-y-hidden overflow-x-auto" data-testid="members-table">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">User</th>
                    <th className="text-left px-4 py-2 font-semibold">Role</th>
                    <th className="text-left px-4 py-2 font-semibold">Joined</th>
                    <th className="text-right px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.members || []).length === 0 && (
                    <tr><td colSpan="4" className="px-4 py-6 text-center text-slate-500">No team members yet. Invite your first below.</td></tr>
                  )}
                  {(data.members || []).map((m) => (
                    <tr key={m.user_id} className="border-b border-slate-100" data-testid={`member-row-${m.user_id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-bold text-slate-900 flex items-center gap-2">
                          {m.is_primary && <Crown className="w-4 h-4 text-amber-500" title="Primary owner" />}
                          {m.name || m.email}
                          {m.is_active ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" title="Active" />
                          ) : (
                            <span className="text-[10px] text-slate-400 uppercase">Pending</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{m.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">{m.org_role}</td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs">
                        {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {!m.is_primary && (
                          <button
                            onClick={() => onRemove(m)}
                            className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 text-xs font-semibold"
                            data-testid={`remove-member-${m.user_id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InviteForm
              disabled={atLimit}
              onInvited={() => load()}
              onSeatLimit={(info) => setUpgradeInfo(info)}
            />
          </>
        )}
      </div>

      <UpgradeModal open={Boolean(upgradeInfo)} info={upgradeInfo} onClose={() => setUpgradeInfo(null)} />
    </div>
  );
};

export default ClientTeamMembers;
